import React from 'react';
import classnames from 'classnames';
import { fabric } from 'sunzi-fabric';
import { MugBottomImage, SynchroObjects } from './mug';
import { Icon } from '@/components';
import { i18n, thumbnail7n } from '@/utils';
import styles from './style.less';

enum FooterType {
  Background,
  Layout,
  Help,
  Next,
  None
}

export interface BottomProps {
  /** 操作提示 */
  tips: string[];
  /** 切换回调 */
  onBottomImageChange: (bottomImage: MugBottomImage) => void;
  /** 底图的图片地址集 */
  bottomImages: MugBottomImage[];
  /** 当前的底图 */
  currentBottomImage?: MugBottomImage;
  /** 画布内的canvas子元素 */
  synchroObjects: SynchroObjects[];
  /** 画布内的激活的canvas元素 */
  synchroActiveObject?: fabric.Object; 
  /** 改变画布内的激活的canvas元素 */
  onActiveObjectChange: (target?: fabric.Object) => void;
  /** 是否是最后的模块 */
  isLastModule: boolean;
  /** 底图下一步禁用状态 */
  bottomNextDisabled: boolean;
  /** 更换底图禁用状态 */
  bottomImageDisabled: boolean;
  layoutVisible: boolean;
  sendBackwards: () => void;
  bringForward: () => void;
  sendToBack: () => void;
  bringToFront: () => void;
  /** 下一步 */
  onNext: () => void;
  onGuideOpen: () => void;
  onLayoutVisible: (visible: boolean) => void;
  gtag: any;
}

export interface BottomState {
  currentTip: string;
  currentType: FooterType,
}

class Bottom extends React.PureComponent<BottomProps, BottomState> {

  private _timer: NodeJS.Timeout | undefined;

  constructor(props: BottomProps) {
    super(props);
    this.state = {
      currentTip: props.tips[0],
      currentType: FooterType.None,
    }
  }

  componentDidMount() {
    const { tips } = this.props;
    this._timer = setInterval(() => {
      const { currentTip } = this.state;
      const index = tips.indexOf(currentTip);
      // 动画完全消失后在展示下一个提示
      this.setState({
        currentTip: ""
      }, () => {
        this.setState({
          currentTip: index >= tips.length - 1 ? tips[0] : tips[index + 1]
        });
      })
    }, 5000);
  }

  componentDidUpdate(preProps: BottomProps) {
    if (preProps.bottomImageDisabled && this.state.currentType === FooterType.Background) {
      this.setState({
        currentType: FooterType.None
      });
    }
  }

  componentWillUnmount() {
    this._timer && clearInterval(this._timer);
  }

  /** 切换底图 */
  handeBottomImageChange = (item: MugBottomImage) => () => {
    const { currentBottomImage, onBottomImageChange } = this.props;
    if (currentBottomImage && currentBottomImage.id === item.id) return;
    onBottomImageChange(item);
  }

  renderContent = () => {
    const { currentType, currentTip } = this.state;
    const { bottomImages, currentBottomImage, synchroActiveObject,
      bringToFront, bringForward, sendBackwards, sendToBack } = this.props;

    if (currentType === FooterType.Background) {
      return  (
        <div className={styles.mugBottomImage}>
          {bottomImages.map(item => {
            const bottomImageItemStyle = classnames(styles.mugBottomImageItem, {
              [styles.mugBottomImageItemActive]: currentBottomImage && currentBottomImage.id === item.id
            });
            return (
              <div key={item.id} className={bottomImageItemStyle} onClick={this.handeBottomImageChange(item)}>
                <div className={styles.imageWrap}>
                  <img src={thumbnail7n(item.thumbnail, 80)} />
                </div>
              </div>
            );
          })}
        </div>
      );
    } else if (synchroActiveObject) {
      return (
        <div className={styles.mugBottomOperator}>
          <div className={styles.mugBottomOperatorItem} onClick={bringToFront}>
            <Icon type="font" size={30} />
            <span>{i18n.format('modules.bottom.operator.top')}</span>
          </div>
          <div className={styles.mugBottomOperatorItem} onClick={bringForward}>
            <Icon type="forward" size={30} />
            <span>{i18n.format('modules.bottom.operator.up')}</span>
          </div>
          <div className={styles.mugBottomOperatorItem} onClick={sendBackwards}>
            <Icon type="backward" size={30} />
            <span>{i18n.format('modules.bottom.operator.down')}</span>
          </div>
          <div className={styles.mugBottomOperatorItem} onClick={sendToBack}>
            <Icon type="back" size={30} />
            <span>{i18n.format('modules.bottom.operator.bottom')}</span>
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.mugBottomTip}> 
          {currentTip}
        </div>
      );
    }
  }

  /**
   * 点击footer item触发
   */
  handleFooterChange = (type: FooterType) => {
    const { currentType } = this.state;
    const { gtag, onNext, bottomImageDisabled, onLayoutVisible, onGuideOpen } = this.props;

    if (type === FooterType.Background) {
      gtag('event', 'mug_bottom_click', {
        'event_category': 'mug',
        'event_label': 'background'
      });
      if (currentType === type || bottomImageDisabled)
        this.setState({
          currentType: FooterType.None
        });
      else
        this.setState({
          currentType: FooterType.Background
        });
    } else {
      if (type === FooterType.Layout) {
        onLayoutVisible(true);
        gtag('event', 'mug_bottom_click', {
          'event_category': 'mug',
          'event_label': 'layout'
        });
      } else if (type === FooterType.Help) {
        onGuideOpen();
        gtag('event', 'mug_bottom_click', {
          'event_category': 'mug',
          'event_label': 'help'
        });
      } else if (type === FooterType.Next) {
        onNext();
        gtag('event', 'mug_bottom_click', {
          'event_category': 'mug',
          'event_label': 'next'
        });
      }
      this.setState({
        currentType: FooterType.None
      });
    }
  }

  // 同步激活元素
  handleActiveObjectChange = (item: fabric.Object) => () => {
    this.props.onActiveObjectChange(item);
  }

  render() {
    const { currentType } = this.state;
    const { isLastModule, synchroObjects, synchroActiveObject, layoutVisible, onLayoutVisible, bottomNextDisabled, bottomImageDisabled } = this.props;
    // 底图样式
    const footerBackgroundStyle = classnames(styles.footerItem, {
      [styles.footerItemActive]: currentType === FooterType.Background,
      [styles.footerItemDisabled]: bottomImageDisabled
    });
    // 下一步样式
    const footerNextStyle = classnames(
      styles.footerItem,
      styles.footerItemNext, {
        [styles.footerItemConfirm]: isLastModule,
        [styles.footerItemDisabled]: bottomNextDisabled
      }
    )

    return (
      <>
        <div className={styles.mugBottom}>
          {this.renderContent()}
          <div className={styles.mugBottomFooter}>
            <div className={footerBackgroundStyle} onClick={() => this.handleFooterChange(FooterType.Background)}>
              <Icon type="background" size={30} />
              <span className={styles.footerItemName}>{i18n.format('modules.bottom.background')}</span>
            </div>
            <div className={styles.footerItem} onClick={() => this.handleFooterChange(FooterType.Layout)}>
              <Icon type="layout" size={30} />
              <span className={styles.footerItemName}>{i18n.format('modules.bottom.layout')}</span>
            </div>
            <div className={styles.footerItem} onClick={() => this.handleFooterChange(FooterType.Help)}>
              <Icon type="question" size={30} />
              <span className={styles.footerItemName}>{i18n.format('modules.bottom.help')}</span>
            </div>
            <div className={footerNextStyle} onClick={() => this.handleFooterChange(FooterType.Next)}>
              {isLastModule ? (
                <>
                  <Icon type="confirm" size={30} />
                  <span className={styles.footerItemName}>{i18n.format('modules.bottom.confirm')}</span>
                </>
              ): (
                <>
                  <Icon type="arrow-right" size={30} />
                  <span className={styles.footerItemName}>{i18n.format('modules.bottom.next')}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={classnames(styles.mugLayout, layoutVisible ? styles.mugLayoutOpen : styles.mugLayoutClose)}>
          <div className={styles.mugLayoutHeader}>
            <div className={styles.mugLayoutHeaderTip}>{i18n.format('modules.bottom.layout.mug.tip')}</div>
            <div className={styles.mugLayoutHeaderClose} onClick={() => onLayoutVisible(false)}>
              <Icon type="close-small" color="#fff" size={15} />
              <span>{i18n.format('modules.bottom.layout.close')}</span>
            </div>
          </div>
          <div className={styles.mugLayoutContent}>
            {synchroObjects.map((item, index) => {
              const contentItemStyle = classnames(
                styles.mugLayoutContentItem, {
                  [styles.mugLayoutContentItemActive]: item.object === synchroActiveObject
                }
              );
              return (
                <div key={index} className={contentItemStyle} onClick={this.handleActiveObjectChange(item.object)}>
                  <div className={styles.imageWrap}>
                    <img src={item.dataURL} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }
}

export default Bottom;